import { useFormikContext } from "formik";
import React from "react";
import ReactMarkdown from "react-markdown";
import { Col, FormGroup, Label, Row } from "reactstrap";
import * as Yup from "yup";
import CheckboxField from "~/components/CheckboxField";
import TextField from "~/components/TextField";
import {
  BODY_MAX_LENGTH,
  DEFAULT_HEADER,
  HEADER_MAX_LENGTH,
  MESSAGE_FLAGS,
  type MessageFlag,
  countCharacters,
} from "~/utils/priority-messages";

export type PriorityMessageFormValues = {
  header: string;
  body: string;
  flags: MessageFlag[];
};

export const priorityMessageSchema = Yup.object({
  header: Yup.string()
    .default(DEFAULT_HEADER)
    .transform((value) => (value?.trim() ? value.trim() : DEFAULT_HEADER))
    .test(
      "max-length",
      `Heading must be ${HEADER_MAX_LENGTH} characters or fewer`,
      (value) => countCharacters(value ?? "") <= HEADER_MAX_LENGTH
    ),
  body: Yup.string()
    .default("")
    .transform((value) => value?.trim() ?? "")
    .test(
      "required",
      "Enter a message",
      (value) => countCharacters(value ?? "") > 0
    )
    .test(
      "max-length",
      `Message must be ${BODY_MAX_LENGTH} characters or fewer`,
      (value) => countCharacters(value ?? "") <= BODY_MAX_LENGTH
    ),
  flags: Yup.array()
    .of(Yup.mixed<MessageFlag>().oneOf(MESSAGE_FLAGS.map((f) => f.value)))
    .default([]) as Yup.Schema<MessageFlag[]>,
});

export const emptyPriorityMessage: PriorityMessageFormValues = {
  header: DEFAULT_HEADER,
  body: "",
  flags: ["cart-always"],
};

export default function PriorityMessageFields() {
  return (
    <>
      <Row>
        <Col md={6}>
          <TextField
            name="header"
            label="Heading"
            characterLimit={HEADER_MAX_LENGTH}
            placeholder={DEFAULT_HEADER}
            description={`Shown as the title above the message. Leave blank for "${DEFAULT_HEADER}".`}
          />

          <TextField
            name="body"
            type="textarea"
            label="Message"
            rows={4}
            autoGrow
            characterLimit={BODY_MAX_LENGTH}
            placeholder="e.g. Online ordering is temporarily unavailable. Please call the store"
            description="Markdown supported - bold, italics, links and lists. The character limit counts the Markdown source, not the rendered text."
          />
        </Col>
        <Col md={6}>
          <MarkdownPreview />
        </Col>
      </Row>

      <FormGroup>
        <Label>Options</Label>
        {MESSAGE_FLAGS.map((flag) => (
          <CheckboxField
            key={flag.value}
            name="flags"
            value={flag.value}
            label={flag.label}
            description={
              flag.value === "cart-always" ? (
                <span style={{ fontWeight: "bold" }}>{flag.description}</span>
              ) : (
                flag.description
              )
            }
          />
        ))}
      </FormGroup>
    </>
  );
}

function MarkdownPreview() {
  const { values } = useFormikContext<PriorityMessageFormValues>();
  const header = values.header?.trim() || DEFAULT_HEADER;
  const body = values.body?.trim() ?? "";

  return (
    <FormGroup>
      <Label>Preview</Label>
      <div
        className="border rounded bg-light p-3"
        style={{ minHeight: "8rem", overflowWrap: "anywhere" }}
      >
        <div className="font-weight-bold">{header}</div>
        {body ? (
          <ReactMarkdown>{body}</ReactMarkdown>
        ) : (
          <span className="text-muted">Nothing to preview yet.</span>
        )}
      </div>
    </FormGroup>
  );
}
